import { TMCERequest } from 'components/Text/RichTextEditorMCETypes';
import { KeyValuePair } from 'utils/array';
import { newGuid } from 'utils/guid';

export enum CopilotConversionItemType {
  User = 0,
  Copilot = 1,
  CopilotQuestion = 2,
  Document = 3,
  CopilotAnswerWithLink = 5,
  SharepointIntegrations = 6,
}

export enum CopilotManagerPromptType {
  Custom = 0,
  Context = 1,
  General = 2,
}

export enum CopilotManagerGeneralPromptType {
  SummarizeContent = 1,
  ImproveWriting = 2,
  SimplifyLanguage = 3,
  ExpandUpon = 4,
}

export enum CopilotErrorType {
  MaxTokens = 1,
}

export enum ContentBlockKind {
  Document = 1,
  Markdown = 2,
  DataTable = 3,
  AdaptiveCard = 4,
  Link = 5,
}

export const CopilotLibraryDocumentEditorAction = 'openLibraryDocumentEditor';

export interface CopilotLibraryDocumentEditorRequest {
  libraryLinkId: number;
  libraryListId?: number;
  title?: string;
  pendingInstruction?: string;
  handoffBlockId?: string;
}

export class CopilotConversationContentBlock {
  id?: string;

  kind: ContentBlockKind;

  version: number;

  created?: Date;

  title?: string;

  text?: string;

  link?: string;

  savedLibraryListId?: number;

  savedLibraryLinkId?: number;

  adaptiveCardJson?: string;

  dataTable?: CopilotDataTableContent;

  adaptiveInputs?: Record<string, string>;

  payload?: unknown;

  mimeType?: string;

  sourceUrl?: string;

  constructor() {
    this.kind = ContentBlockKind.Markdown;
    this.version = 0;
  }

  clone(): CopilotConversationContentBlock {
    const clone = new CopilotConversationContentBlock();
    clone.id = this.id;
    clone.kind = this.kind;
    clone.version = this.version;
    clone.created = this.created ? new Date(this.created) : undefined;
    clone.title = this.title;
    clone.text = this.text;
    clone.link = this.link;
    clone.savedLibraryListId = this.savedLibraryListId;
    clone.savedLibraryLinkId = this.savedLibraryLinkId;
    clone.adaptiveCardJson = this.adaptiveCardJson;
    clone.dataTable = this.dataTable?.clone();
    clone.adaptiveInputs = this.adaptiveInputs ? { ...this.adaptiveInputs } : undefined;
    clone.payload = this.payload;
    clone.mimeType = this.mimeType;
    clone.sourceUrl = this.sourceUrl;

    return clone;
  }
}

export class CopilotDataTableContent {
  columns: CopilotDataTableColumn[];

  rows: Record<string, unknown>[];

  page: number;

  pageSize: number;

  totalCount: number;

  returnedCount: number;

  isTruncated: boolean;

  maxReturnedRows: number;

  constructor() {
    this.columns = [];
    this.rows = [];
    this.page = 0;
    this.pageSize = 0;
    this.totalCount = 0;
    this.returnedCount = 0;
    this.isTruncated = false;
    this.maxReturnedRows = 0;
  }

  clone(): CopilotDataTableContent {
    const clone = new CopilotDataTableContent();
    clone.columns = this.columns.map((column) => column.clone());
    clone.rows = this.rows.map((row) => ({ ...row }));
    clone.page = this.page;
    clone.pageSize = this.pageSize;
    clone.totalCount = this.totalCount;
    clone.returnedCount = this.returnedCount;
    clone.isTruncated = this.isTruncated;
    clone.maxReturnedRows = this.maxReturnedRows;

    return clone;
  }
}

export class CopilotDataTableColumn {
  key: string;

  name: string;

  dataType?: string;

  constructor() {
    this.key = '';
    this.name = '';
  }

  clone(): CopilotDataTableColumn {
    const clone = new CopilotDataTableColumn();
    clone.key = this.key;
    clone.name = this.name;
    clone.dataType = this.dataType;

    return clone;
  }
}

export class CopilotConversation {
  id?: string;

  request?: CopilotConversationItem;

  thread: CopilotConversationItem[];

  constructor() {
    this.thread = [];
  }

  hasData = (): boolean => {
    return this.thread.length > 0;
  };

  setRequestFromRTE = (request: TMCERequest) => {
    this.request = new CopilotConversationItem();
    this.request.question = request.query;
    this.request.context = request.context;
    this.setPromptTypes(request);
  };

  setRequestFromQuery = (query: string): CopilotConversationItem => {
    this.request = new CopilotConversationItem();
    this.request.question = query;
    this.request.type = CopilotConversionItemType.User;
    this.request.promptType = CopilotManagerPromptType.Custom;

    return this.request;
  };

  setRequestFromEntitySelection = (selection: number[]): CopilotConversationItem => {
    this.request = new CopilotConversationItem();
    this.request.type = CopilotConversionItemType.User;
    this.request.promptType = CopilotManagerPromptType.Custom;
    this.request.selectedEntityIds = selection;

    return this.request;
  };

  clone = (): CopilotConversation => {
    const clone = new CopilotConversation();
    clone.id = this.id;
    clone.request = this.request?.clone();
    clone.thread = this.thread?.map((item) => item.clone());

    return clone;
  };

  private setPromptTypes = (request: TMCERequest) => {
    if (!this.request) return;
    switch (request.query) {
      case 'FillInField':
        this.request.promptType = CopilotManagerPromptType.Context;
        break;
      case 'SummarizeContent':
        this.request.promptType = CopilotManagerPromptType.General;
        this.request.generalPromptType = CopilotManagerGeneralPromptType.SummarizeContent;
        break;
      case 'ImproveWriting':
        this.request.promptType = CopilotManagerPromptType.General;
        this.request.generalPromptType = CopilotManagerGeneralPromptType.ImproveWriting;
        break;
      case 'SimplifyLanguage':
        this.request.promptType = CopilotManagerPromptType.General;
        this.request.generalPromptType = CopilotManagerGeneralPromptType.SimplifyLanguage;
        break;
      case 'ExpandUpon':
        this.request.promptType = CopilotManagerPromptType.General;
        this.request.generalPromptType = CopilotManagerGeneralPromptType.ExpandUpon;
        break;
      default:
        this.request.promptType = CopilotManagerPromptType.Custom;
        break;
    }
  };
}

export class CopilotResponseAnnotations {
  citations?: PromptResponseCitation[];

  metadata?: KeyValuePair[];

  hasData = (): boolean => {
    return !!(this.citations && this.citations.length > 0) || !!(this.metadata && this.metadata.length > 0);
  };
}

export class CopilotConversationBaseItem extends CopilotResponseAnnotations {
  question?: string;

  context?: string;

  promptType?: CopilotManagerPromptType;

  generalPromptType?: CopilotManagerGeneralPromptType;

  response?: string;

  responseRaw?: string;

  selectedEntityIds?: number[];

  link?: string;

  contentBlocks?: CopilotConversationContentBlock[];

  action?: () => Promise<void>;
}

export class CopilotConversationItem extends CopilotConversationBaseItem {
  id?: string;

  type: CopilotConversionItemType;

  date?: Date;

  userId?: string;

  copilotStatus?: string;

  constructor() {
    super();
    this.id = newGuid();
    this.date = new Date();
    this.type = CopilotConversionItemType.User;
  }

  clone(): CopilotConversationItem {
    const clone = new CopilotConversationItem();
    clone.id = this.id;
    clone.question = this.question;
    clone.context = this.context;
    clone.promptType = this.promptType;
    clone.generalPromptType = this.generalPromptType;
    clone.response = this.response;
    clone.responseRaw = this.responseRaw;
    clone.type = this.type;
    clone.date = this.date ? new Date(this.date) : undefined;
    clone.userId = this.userId;
    clone.copilotStatus = this.copilotStatus;
    clone.citations = this.citations?.map((c) => c.clone()) || [];
    clone.metadata = this.metadata ? [...this.metadata] : undefined;
    clone.link = this.link;
    clone.selectedEntityIds = this.selectedEntityIds ? [...this.selectedEntityIds] : undefined;
    clone.contentBlocks = this.contentBlocks?.map((block) => block.clone());

    return clone;
  }
}

export class PromptResponseCitation {
  title?: string;

  uri?: string;

  clone(): PromptResponseCitation {
    const clone = new PromptResponseCitation();
    clone.title = this.title;
    clone.uri = this.uri;

    return clone;
  }
}
